import { useTranslation } from "react-i18next";
import { Box, Paper, Skeleton, Stack, Text, Title } from "@mantine/core";
import {
  IconBroadcast,
  IconSend,
  IconServerCog,
  IconShieldLock,
  IconUserCircle,
  IconWaveSine,
  IconWorld,
  type Icon,
} from "@tabler/icons-react";
import { useSettings } from "../api/admin";
import type { Setting } from "../api/types";
import { useLink, usePath } from "../router";
import { NavBar } from "../ui/NavBar";
import { useLayout } from "../ui/useLayout";
import { SettingRow } from "./SettingRow";

export interface SettingsTab {
  /** Also the URL segment: `/admin/settings/<id>`. */
  id: string;
  /** The mark before the label. A component, not an element: this table is data. */
  icon: Icon;
  /** Groups in the order they read, not alphabetically. */
  groups: readonly string[];
}

/**
 * The seven tabs, and which categories each holds.
 *
 * One flat page of 150 rows is a scroll, not a structure: the thing you came to change is
 * somewhere in it and nothing tells you where. These are the seven questions the settings
 * actually answer -- how the stream becomes shows, how the audio is caught and kept, what a
 * listener sees, where a finished show goes, who has an account, what the API refuses, where
 * the other processes are -- and a setting lives in the one its answer belongs to.
 *
 * The group names are the server's own identifiers, which are also the catalogue keys, so a
 * group is named in exactly two places: `settings_store` declares it, this table places it.
 */
export const TABS: readonly SettingsTab[] = [
  {
    id: "archive",
    icon: IconWaveSine,
    groups: ["Classification", "Show boundaries", "Event matching", "Track recognition"],
  },
  {
    id: "recording",
    icon: IconBroadcast,
    groups: ["Upstream", "Recorder instances", "Edge workers", "Storage"],
  },
  { id: "site", icon: IconWorld, groups: ["Public site", "News", "Cover art", "Chat"] },
  { id: "publishing", icon: IconSend, groups: ["Publishing", "Telegram", "VK"] },
  {
    id: "accounts",
    icon: IconUserCircle,
    groups: ["Registration", "Administrator and sessions", "Sign-in providers", "Email"],
  },
  {
    id: "security",
    icon: IconShieldLock,
    groups: ["Network access", "Response headers", "Login limits", "Captcha"],
  },
  // Last, and so also the tab a group no table here lists falls into -- see `sectionsOf`.
  { id: "system", icon: IconServerCog, groups: ["Services", "Proxies", "Observability", "Other"] },
];

const LAST_TAB = TABS[TABS.length - 1] as SettingsTab;

/**
 * Which tab a path is in.
 *
 * Falls back to the first rather than to none, because bare `/admin` renders this page and a
 * bar with nothing lit reads as a bar nobody has clicked yet.
 */
export function tabOf(path: string): SettingsTab {
  return TABS.find((tab) => path === `/admin/settings/${tab.id}`) ?? (TABS[0] as SettingsTab);
}

/**
 * One tab's sections, in reading order, skipping any the server sent nothing for.
 *
 * A group no tab lists lands in the last tab rather than vanishing: the server decides which
 * group a key is in, so a newer build inventing one must not make its settings unreachable
 * from the page that configures them.
 */
export function sectionsOf(settings: Setting[], tab: SettingsTab): Array<[string, Setting[]]> {
  const byGroup = new Map<string, Setting[]>();
  for (const setting of settings) {
    const rows = byGroup.get(setting.group);
    if (rows) rows.push(setting);
    else byGroup.set(setting.group, [setting]);
  }
  const titles = tab.groups.filter((title) => byGroup.has(title));
  if (tab.id === LAST_TAB.id) {
    const unlisted = [...byGroup.keys()].filter(
      (title) => !TABS.some((entry) => entry.groups.includes(title)),
    );
    titles.push(...unlisted.sort());
  }
  return titles.map((title) => [title, byGroup.get(title) as Setting[]]);
}

export function SettingsPage() {
  const { data, isLoading } = useSettings();
  const { t } = useTranslation("admin");
  const path = usePath();
  // Seven labelled slots do not fit a phone row, and the bar does not wrap: the marks carry
  // them, the same trade the admin section bar makes two bands above this one.
  const { phone } = useLayout();
  const tab = tabOf(path);

  if (isLoading)
    return (
      <Paper p={phone ? 12 : "md"} m={phone ? 12 : 20} style={{ overflowY: "auto", minHeight: 0 }}>
        <Skeleton height={24} width={200} mb={6} />
        <Skeleton height={14} width="100%" maw="60ch" mb={14} />
        <Stack gap={12}>
          {[0, 1, 2, 3, 4, 5].map((row) => (
            <Skeleton key={row} height={36} />
          ))}
        </Stack>
      </Paper>
    );
  const settings = data ?? [];

  return (
    <Paper p={phone ? 12 : "md"} m={phone ? 12 : 20} style={{ overflowY: "auto", minHeight: 0 }}>
      <Title order={2} fz="lg" lh={1.5} mb={6}>
        {t("settingsPage.heading")}
      </Title>
      <Text size="xs" c="dimmed" maw="70ch" mb={14}>
        {t("settingsPage.intro")}
      </Text>

      {/* The rule NavBar's slots lap over: its selected underline sits a pixel past the bar's
          own bottom edge, and without a line there it reads as a stray mark. */}
      <Box mb={20} style={{ borderBottom: "1px solid var(--mantine-color-default-border)" }}>
        <NavBar label={t("settingsPage.tabsLabel")} value={`/admin/settings/${tab.id}`}>
          {TABS.map((entry) => (
            <TabSlot key={entry.id} tab={entry} current={tab.id} compact={phone} />
          ))}
        </NavBar>
      </Box>

      {sectionsOf(settings, tab).map(([title, rows]) => {
        // The group arrives from the server as an English identifier rather than as copy --
        // it is the map key both halves of this lookup share. A group a newer server invents
        // falls back to its own name and no blurb, which is what the last tab already does
        // for the section itself.
        const blurb = t(`settingsPage.blurb.${title}`, { defaultValue: "" });
        return (
          <Stack key={title} component="section" gap="sm" mb={26} maw={900}>
            <Title order={3} fz="sm" lh={1.5} mb={4}>
              {t(`settingsPage.group.${title}`, { defaultValue: title })}
            </Title>
            {blurb && (
              <Text size="xs" c="dimmed" maw="70ch" mb={14}>
                {blurb}
              </Text>
            )}
            {rows.map((setting) => (
              <SettingRow key={setting.key} setting={setting} />
            ))}
          </Stack>
        );
      })}
    </Paper>
  );
}

function TabSlot({
  tab,
  current,
  compact,
}: {
  tab: SettingsTab;
  current: string;
  compact: boolean;
}) {
  const { t } = useTranslation("admin");
  const path = `/admin/settings/${tab.id}`;
  const link = useLink(path);
  const Mark = tab.icon;
  return (
    <NavBar.Slot
      value={path}
      href={link.href}
      onClick={link.onClick}
      active={current === tab.id}
      icon={<Mark size={15} stroke={1.5} aria-hidden="true" />}
      label={t(`settingsPage.tab.${tab.id}`)}
      compact={compact}
    />
  );
}
